import React from 'react';
import {
    Modal, Button, Typography, Row, Divider, Spin, Select, Space, Avatar, Form
} from 'antd';
import {
    MinusOutlined, PlusOutlined, CopyOutlined
} from '@ant-design/icons';
import {QRCode} from 'react-qrcode-logo';
import SyntaxHighlighter from 'react-syntax-highlighter';
import {a11yLight} from 'react-syntax-highlighter/dist/esm/styles/hljs';
import shortUUID from 'short-uuid';
import ApiService from '../services/ApiService';
import MultiLang from '../components/custom-fields/MultiLang';
import MultiDevise from '../components/custom-fields/MultiDevise';
import {WEB_BASE_URL, MOBCOLORS} from '../configs/AppConfig';
import {getConfig} from '../components/permissions/functions';
import {connect} from 'react-redux';

const {Text, Title} = Typography;
const DEFAULTSIZE = 200;
const IFRAME_TEMPLATE = `<iframe id="kehila-iframe" style="border:0;width: 1px;min-width: 100%;height:100%" frameborder="0"></iframe>
<script type="text/javascript">
    let pageUrl="{{url}}?render=embed";
    const searchParams=new URLSearchParams(window.location.search);
    if(searchParams){ pageUrl+="&"+searchParams.toString(); }
    document.getElementById("kehila-iframe").src=pageUrl;
</script>`;

// ======================================================
// 1️⃣  SERVICES
// ======================================================

const LinkAPIService = {
    async create(type, payload, url = null) {
        let res = null;
        switch (type) {
            case 'PAIEMENT':
            case 'UPDATE-PAIEMENT':
                res = await ApiService.post('/links/' + type, payload);
                break;
            case 'REDIRECT':
                res = await ApiService.post('/links/' + type, {type: 'URL', target: url});
                break;
            default:
                return null;
        }
        return res?.success ? res.url : null;
    },
};

const LinkBuilderService = {
    getVirtual(org, object) {
        const assoc = object?.assoc || object?.form?.assoc;
        const o = {...org};
        if (assoc && Array.isArray(o.virtuals)) {
            const virtual = o.virtuals.find(v => v.assocs?.includes(assoc));
            if (virtual) {
                o.prefix = virtual.prefix;
                o.domain = virtual.domain;
                o.VIRTUAL_BASE_WEB_URL = virtual.domain
                    ? 'https://' + virtual.domain
                    : WEB_BASE_URL + '/' + virtual.prefix;
            }
        }
        return o;
    },

    getDevisesUrl(org) {
        const o = {...org};
        if (Array.isArray(o.devisesInterfaces)) {
            o.devisesUrl = o.devisesInterfaces.map(di => {
                const base = di.domain
                    ? 'https://' + di.domain
                    : WEB_BASE_URL + '/' + di.prefix;
                return {devise: di.devise, url: base};
            });
        }
        return o;
    },

    buildUrl({org, url, lang, devise, orgDevise, embedConfig, accessId, accessList, baseUrl, filterProps, filterData}) {
        let finalUrl = url;
        if (embedConfig?.iframeUrl) finalUrl = embedConfig.iframeUrl;
        if (filterProps) finalUrl = filterProps.filterFunction(filterData, finalUrl);

        // Langue
        if (lang && !embedConfig?.iframeUrl && url?.includes(baseUrl)) {
            finalUrl = finalUrl.replace(baseUrl, baseUrl + '/' + lang.value);
        }

        // Devise
        if (orgDevise !== devise && org.devisesUrl?.length) {
            const target = org.devisesUrl.find(i => i.devise === devise);
            if (target) finalUrl = finalUrl.replace(org.BASE_WEB_URL, target.url);
        }

        // Virtuals
        if (org.VIRTUAL_BASE_WEB_URL) {
            finalUrl = finalUrl.replace(org.BASE_WEB_URL, org.VIRTUAL_BASE_WEB_URL);
        }

        // Access param
        if (accessId && finalUrl) {
            const acc = accessList.find(i => i.id === accessId);
            const a = acc?.shortName || shortUUID().fromUUID(accessId);
            finalUrl += finalUrl.includes('?') ? '&a=' + a : '?a=' + a;
        }

        return finalUrl;
    },
};

// ======================================================
// 2️⃣  SOUS-COMPOSANTS
// ======================================================

function RenderFilter({initialValues, items, onChange}) {
    return (
        <Form initialValues={initialValues} onValuesChange={(_, data) => onChange(data)}>
            {items.map(item => (
                <Form.Item key={item.name} name={item.name} label={item.label}>
                    {item.input}
                </Form.Item>
            ))}
        </Form>
    );
}

class LinkTab extends React.Component {
    state = {link: null, size: DEFAULTSIZE, loading: false};

    async componentDidMount() {
        const {type} = this.props;
        if (type !== 'REDIRECT') await this.generateLink();
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.url !== this.props.url || prevProps.payload !== this.props.payload) {
            if (this.props.type !== 'REDIRECT') await this.generateLink();
        }
    }

    async generateLink() {
        const {type, payload, url, org, disableShortLink} = this.props;
        if (disableShortLink) return;
        this.setState({loading: true});
        let linkUrl = await LinkAPIService.create(type, payload, url);
        if (org.VIRTUAL_BASE_WEB_URL)
            linkUrl = linkUrl.replace(org.BASE_WEB_URL, org.VIRTUAL_BASE_WEB_URL).replace('//', '/');
        this.setState({link: linkUrl, loading: false});
    }

    changeSize(delta) {
        const s = this.state.size + delta;
        if (s > 0) this.setState({size: s});
    }

    downloadQRCode() {
        const canvas = document.getElementById('custom-qrcode');
        if (canvas) {
            const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
            const link = document.createElement('a');
            link.href = pngUrl;
            link.download = 'qrcode.png';
            link.click();
        }
    }

    render() {
        const {type, url, disableShortLink} = this.props;
        const {link, size, loading} = this.state;

        return (
            <Spin spinning={loading}>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 10}}>
                    {type === 'REDIRECT' && (
                        <Text copyable={{text: url}} style={{textAlign:"center"}}>
                            <a href={url} target="_blank" style={{color: MOBCOLORS.blue.color}}>{url}</a>
                        </Text>
                    )}

                    {!disableShortLink && !link && type === 'REDIRECT' && (
                        <>
                            <Divider/>
                            <Button onClick={() => this.generateLink()}>QR code / Url Raccourci</Button>
                        </>
                    )}

                    {link && (
                        <>
                            {type === 'REDIRECT' && <Divider/>}
                            <Title level={4}>Lien Raccourci</Title>
                            <Text copyable={{text: link}}>
                                <a href={link} target="_blank" style={{color: MOBCOLORS.blue.color}}>{link}</a>
                            </Text>

                            <Row style={{margin: '20px 0'}}>
                                <Button icon={<MinusOutlined/>} onClick={() => this.changeSize(-20)} style={{marginRight: 10}}>
                                    Plus petit
                                </Button>
                                <Button icon={<PlusOutlined/>} onClick={() => this.changeSize(20)}>
                                    Plus grand
                                </Button>
                            </Row>

                            <a onClick={() => this.downloadQRCode()}>
                                <QRCode
                                    id="custom-qrcode"
                                    value={link}
                                    size={size}
                                    eyeRadius={10}
                                    removeQrCodeBehindLogo
                                    ecLevel="H"
                                    enableCORS
                                />
                            </a>
                        </>
                    )}
                </div>
            </Spin>
        );
    }
}

function IframeCode({url}) {
    const finalCode = IFRAME_TEMPLATE.replace('{{url}}', url);
    return (
        <div>
            <Row style={{justifyContent: 'flex-end'}}>
                <Button shape="circle" icon={<CopyOutlined/>}
                        onClick={() => navigator.clipboard.writeText(finalCode)}/>
            </Row>
            <SyntaxHighlighter language="javascript" showLineNumbers wrapLines style={a11yLight}>
                {finalCode}
            </SyntaxHighlighter>
        </div>
    );
}

// ======================================================
// 3️⃣  CLASSE PRINCIPALE : MODAL
// ======================================================

class ModalLink extends React.Component {
    constructor(props) {
        super(props);
        props.getRef(this);
        this.state = {
            visible: false,
            url: null,
            payload: null,
            type: null,
            filterData: {},
            filterProps: null,
            embedConfig: null,
            disableShortLink: false,
            baseUrl: null,
            accessId: null,
            options: {},
            object: null,
        };
    }

    // ===== API externe =====
    setOptions(options = {}) {
        this.setState({options});
        return this;
    }

    setObject(object) {
        this.setState({object});
        return this;
    }

    async getLink(type, payload) {
        const isAttrSystem = getConfig('AttrSystem', 'CONFIG');
        if (isAttrSystem) this.setState({accessId: this.props.access.id});
        this.setState({visible: true, type, payload});
    }

    async withLink(url, filterProps = null, embedConfig = null) {
        let initialValues = {};
        if (filterProps) {
            for (const item of filterProps.filters || []) {
                if (item.initialValue !== undefined) initialValues[item.name] = item.initialValue;
            }
        }
        const isAttrSystem = getConfig('AttrSystem', 'CONFIG');
        if (isAttrSystem && !filterProps) this.setState({accessId: this.props.access.id});
        this.setState({
            visible: true,
            type: 'REDIRECT',
            url,
            embedConfig,
            filterProps,
            filterData: initialValues,
            disableShortLink: filterProps?.disableShortLink,
            baseUrl: filterProps?.baseUrl,
        });
    }

    close() {
        this.setState({visible: false});
    }

    render() {
        const {url, type, payload, object} = this.state;
        let org = this.props.org;
        org = LinkBuilderService.getVirtual(org, object);
        org = LinkBuilderService.getDevisesUrl(org);
        const orgDevise = org.devise || 'eur';

        return (
            <Modal
                style={{maxWidth: '90vw'}}
                title="Récupérer le Lien / QRCode"
                open={this.state.visible}
                destroyOnClose
                closable
                footer={null}
                onCancel={() => this.close()}
            >
                {this.state.filterProps && (
                    <RenderFilter
                        initialValues={this.state.filterData}
                        items={this.state.filterProps.filters || []}
                        onChange={data => this.setState({filterData: data})}
                    />
                )}

                {this.state.accessId && (
                    <Row style={{justifyContent: 'flex-end', marginBottom: 10}}>
                        <Select
                            size="large"
                            value={this.state.accessId}
                            onChange={v => this.setState({accessId: v})}
                        >
                            {this.props.org.accessList.map(al => {
                                const bgColor = al.color || '#000';
                                const shortName = al.shortName || al.prenom.slice(0, 3).toUpperCase();
                                return (
                                    <Select.Option key={al.id} value={al.id}>
                                        <Space>
                                            <Avatar size="small" style={{backgroundColor: bgColor, color: '#FFF'}}>
                                                {shortName}
                                            </Avatar>
                                            <span>{al.prenom} {al.nom}</span>
                                        </Space>
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Row>
                )}

                <MultiDevise
                    forceDevise={this.state.options.forceDevise}
                    disabled={!this.state.options.multiDevise}
                >
                    {devise => (
                        <MultiLang disabled={this.state.embedConfig?.iframeUrl}>
                            {lang => {
                                const finalUrl = LinkBuilderService.buildUrl({
                                    org, url, lang, devise, orgDevise,
                                    embedConfig: this.state.embedConfig,
                                    accessId: this.state.accessId,
                                    accessList: this.props.org.accessList,
                                    baseUrl: this.state.baseUrl || org.BASE_WEB_URL,
                                    filterProps: this.state.filterProps,
                                    filterData: this.state.filterData,
                                });
                                const _payload = {...payload};
                                if (lang?.value) _payload.lang = lang.value;
                                if (orgDevise !== devise) _payload.devise = devise;
                                if (this.state.accessId) _payload.accessId = this.state.accessId;

                                if (this.state.embedConfig?.iframeUrl) {
                                    return (
                                        <div>
                                            <Divider/>
                                            <LinkTab
                                                org={org}
                                                type={type}
                                                payload={_payload}
                                                url={finalUrl}
                                                disableShortLink={this.state.disableShortLink}
                                            />
                                            <Divider/>
                                            <IframeCode url={url}/>
                                        </div>
                                    );
                                }

                                return (
                                    <LinkTab
                                        org={org}
                                        type={type}
                                        payload={_payload}
                                        url={finalUrl}
                                        disableShortLink={this.state.disableShortLink}
                                    />
                                );
                            }}
                        </MultiLang>
                    )}
                </MultiDevise>
            </Modal>
        );
    }
}

// ======================================================
// 4️⃣  EXPORT
// ======================================================

const mapStateToProps = state => ({
    org: state.auth.org,
    access: state.auth.access,
});

export default connect(mapStateToProps)(ModalLink);


// ======================================================
//  🧠 LINK BUILDER API — version simplifiée (sans MultiLang)
// ======================================================

/**
 * Normalise l'objet org (virtuals, devises)
 */
function prepareOrg(org, object = null) {
    org = LinkBuilderService.getVirtual(org, object);
    org = LinkBuilderService.getDevisesUrl(org);
    return org;
}

/**
 * Construit l'URL complète, sans gestion de langue
 */
function buildAutoUrl(org, baseUrl, options = {}) {
    let finalUrl = baseUrl;
    const {devise, orgDevise, accessId, accessList} = options;

    // Multi-devise
    if (orgDevise !== devise && Array.isArray(org.devisesUrl)) {
        const target = org.devisesUrl.find(i => i.devise === devise);
        if (target) {
            finalUrl = finalUrl.replace(org.BASE_WEB_URL, target.url);
        }
    }

    // Virtual domain
    if (org.VIRTUAL_BASE_WEB_URL) {
        finalUrl = finalUrl.replace(org.BASE_WEB_URL, org.VIRTUAL_BASE_WEB_URL);
    }

    // Access paramètre
    if (accessId && accessList) {
        const acc = accessList.find(i => i.id === accessId);
        const a = acc?.shortName || shortUUID().fromUUID(accessId);
        finalUrl += finalUrl.includes('?') ? `&a=${a}` : `?a=${a}`;
    }

    return finalUrl;
}

/**
 * 🔗 Crée un lien REDIRECT (avec raccourcissement backend)
 */
export function getRedirectLink(url, org, access, options = {}) {
    org = prepareOrg(org, options.object);
    const orgDevise = org.devise || 'eur';

    const finalUrl = buildAutoUrl(org, url, {
        devise: options.devise || orgDevise,
        orgDevise,
        accessId: access?.id,
        accessList: org.accessList,
    });

    return {
        type: 'REDIRECT',
        url: finalUrl,
    };
}

/**
 * 💰 Crée un lien de paiement
 */
export async function createPaymentLink(payload, org, access, options = {}) {
    org = prepareOrg(org, options.object);
    const orgDevise = org.devise || 'eur';

    const data = {...payload};
    const devise = options.devise || orgDevise;
    if (devise && orgDevise !== devise) data.devise = devise;
    if (access?.id) data.accessId = access.id;

    const shortUrl = await LinkAPIService.create('PAIEMENT', data);

    return {
        type: 'PAIEMENT',
        url:shortUrl,
    };
}

/**
 * 🔄 Crée un lien de mise à jour de paiement (UPDATE-PAIEMENT)
 */
export async function createUpdatePaymentLink(payload, org, access, options = {}) {
    org = prepareOrg(org, options.object);
    const orgDevise = org.devise || 'eur';

    const data = {...payload};
    const devise = options.devise || orgDevise;
    if (devise && orgDevise !== devise) data.devise = devise;
    if (access?.id) data.accessId = access.id;

    const shortUrl = await LinkAPIService.create('UPDATE-PAIEMENT', data);

    return {
        type: 'UPDATE-PAIEMENT',
        url:shortUrl,
    };
}


//@legacy
export async function requestLinkPayload(type, payload, url = null) {
    var res = null;
    switch (type) {
        case 'PAIEMENT':
        case 'UPDATE-PAIEMENT':
            res = await ApiService.post('/links/' + type, payload);
            break;
        case 'REDIRECT':
            res = await ApiService.post('/links/' + type, { type: 'URL', target: url });
            break;
    }
    if (res?.success) {
        return res.url;
    }
    return null;
}